// Phase 45 — Day-detail drawer for the manager calendar. Triggered by
// tapping any day header / empty area on the 7-day OR month view. On
// mobile it slides up as a bottom sheet (90vh); on desktop it sits as a
// centered modal (max-w-3xl, 80vh). Inside the drawer the manager sees
// the day's bookings, travel pills between same-staff jobs, pending
// enquiries, and two big buttons to add a new booking or enquiry — the
// canonical creation paths now that the legacy "tap-day-to-create" flow
// is gone.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, X, Plus, Hourglass, Clock, MapPin, Route, Coins,
} from "lucide-react";
import { linkifyText } from "@/lib/linkify";

// Friendly weekday + dd/mm/yy header label. Kept here to avoid an
// import cycle back into Calendar.jsx.
function dayTitle(d) {
  return d.toLocaleDateString(undefined, {
    weekday: "long", day: "2-digit", month: "2-digit", year: "2-digit",
  });
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return "—"; }
}

function endTime(b) {
  try {
    const start = new Date(b.scheduled_start);
    const mins = Number(b?.template_snapshot?.duration_minutes || 60);
    const end = new Date(start.getTime() + mins * 60000);
    return end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return "—"; }
}

// Small left-stripe palette — deterministic per staff_id. Mirrors the
// `staffColor` function in Calendar.jsx so the drawer's stripes line up
// with the 7-day grid's stripes.
const STAFF_COLOR_HEX = [
  "rgb(245 158 11)", "rgb(2 132 199)", "rgb(5 150 105)",
  "rgb(225 29 72)", "rgb(124 58 237)", "rgb(13 148 136)",
];
function staffStripe(staffId) {
  if (!staffId) return STAFF_COLOR_HEX[0];
  let hash = 0;
  for (const ch of staffId) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return STAFF_COLOR_HEX[Math.abs(hash) % STAFF_COLOR_HEX.length];
}

// Mini payment-type colored dot — matches the 7-day tile dots.
function paymentDotClass(pt) {
  switch (pt) {
    case "simple_cash": return "bg-emerald-500";
    case "vat": return "bg-blue-500";
    case "rct": return "bg-zinc-500";
    case "cash_with_invoice": return "bg-amber-500";
    default: return "bg-zinc-300";
  }
}

function paymentLabel(pt) {
  switch (pt) {
    case "simple_cash": return "Cash";
    case "cash_with_invoice": return "Cash + Inv";
    case "vat": return "VAT";
    case "rct": return "RCT";
    default: return pt || "—";
  }
}

export default function DayDetailDrawer({
  open, onOpenChange, date, bookings = [], enquiries = [],
  onOpenBooking, onAddBooking, onAddEnquiry, onPrevDay, onNextDay,
  onEditEnquiry,
}) {
  // Local copy so the loader spinner doesn't flash empty while the page
  // re-fetches after a status change.
  const dayList = useMemo(() => {
    if (!date) return [];
    return bookings
      .filter((b) => isSameDay(new Date(b.scheduled_start), date))
      .sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
  }, [bookings, date]);

  const dayEnqs = useMemo(() => {
    if (!date) return [];
    return enquiries
      .filter((e) => e.tentative_start && isSameDay(new Date(e.tentative_start), date))
      .sort((a, b) => new Date(a.tentative_start) - new Date(b.tentative_start));
  }, [enquiries, date]);

  // Stats strip: jobs · travel time · cash count · €expected
  const stats = useMemo(() => {
    let totalTravelMin = 0;
    let cashCount = 0;
    let expected = 0;
    for (const b of dayList) {
      totalTravelMin += Number((b.travel_from_previous || {}).duration_min || 0);
      if (b.payment_type_code === "simple_cash" || b.payment_type_code === "cash_with_invoice") {
        cashCount += 1;
      }
      // Sum line-item amounts when available, else fall back to template price.
      const lineTotal = (b.line_items || []).reduce(
        (s, ln) => s + Number(ln.amount_ex_vat || ln.amount || ln.unit_price || 0),
        0,
      );
      expected += lineTotal || Number(b?.template_snapshot?.price || 0);
    }
    const hrs = Math.floor(totalTravelMin / 60);
    const mins = Math.round(totalTravelMin - hrs * 60);
    const travelLabel = totalTravelMin > 0
      ? (hrs > 0 ? `${hrs}h ${String(mins).padStart(2, "0")}m` : `${mins}m`)
      : "—";
    return { jobs: dayList.length, travel: travelLabel, cashCount, expected };
  }, [dayList]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] max-w-3xl p-0 overflow-hidden h-[90vh] sm:h-[80vh] flex flex-col sm:rounded-[10px] rounded-t-[16px] sm:rounded-b-[10px] data-[state=open]:slide-in-from-bottom-4"
        data-testid="day-detail-drawer"
      >
        <DialogTitle className="sr-only">
          {date ? `Bookings for ${dayTitle(date)}` : "Day detail"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Day-detail drawer listing the day's bookings, pending enquiries, and add controls.
        </DialogDescription>
        {/* Header — prev/next arrows + day title + close */}
        {date && (
          <div className="border-b border-border px-4 sm:px-5 py-3 flex items-center justify-between gap-2 bg-card sticky top-0 z-10">
            <button
              type="button"
              onClick={onPrevDay}
              className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-secondary transition-colors"
              data-testid="day-drawer-prev"
              aria-label="Previous day"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 text-center min-w-0">
              <div className="text-base sm:text-lg font-extrabold truncate" data-testid="day-drawer-title">
                {dayTitle(date)}
              </div>
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {stats.jobs} job{stats.jobs === 1 ? "" : "s"} · {stats.travel} travel · {stats.cashCount} cash · {formatCurrency(stats.expected)}
              </div>
            </div>
            <button
              type="button"
              onClick={onNextDay}
              className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-secondary transition-colors"
              data-testid="day-drawer-next"
              aria-label="Next day"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="ml-1 h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-secondary transition-colors"
              data-testid="day-drawer-close"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-3" data-testid="day-drawer-body">
          {dayList.length === 0 && dayEnqs.length === 0 && (
            <div className="border border-dashed border-border rounded-[8px] py-10 text-center text-sm text-muted-foreground">
              No bookings or enquiries on this day.
            </div>
          )}

          {(() => {
            // Interleave travel pills between consecutive same-staff jobs.
            const prevByStaff = {};
            const out = [];
            for (const b of dayList) {
              const prev = prevByStaff[b.staff_id] || null;
              if (b.travel_from_previous && prev) {
                out.push(
                  <DrawerTravelPill key={`tp-${b.id}`} travel={b.travel_from_previous} prev={prev} next={b} />,
                );
              }
              out.push(
                <DrawerBookingCard
                  key={b.id}
                  b={b}
                  onClick={() => onOpenBooking?.(b.id)}
                />,
              );
              prevByStaff[b.staff_id] = b;
            }
            return out;
          })()}

          {dayEnqs.length > 0 && (
            <div className="pt-3" data-testid="day-drawer-enquiries-section">
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-amber-700 mb-2 flex items-center gap-1.5">
                <Hourglass className="h-3 w-3" /> Pending enquiries
              </div>
              <div className="space-y-2">
                {dayEnqs.map((enq) => (
                  <button
                    type="button"
                    key={enq.id}
                    onClick={() => onEditEnquiry?.(enq)}
                    className="w-full text-left bg-amber-50/70 border-2 border-amber-400 border-dashed rounded-[6px] px-3 py-2 hover:border-amber-600 hover:bg-amber-50 transition-colors"
                    data-testid={`day-drawer-enquiry-${enq.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="mono font-bold text-sm tabular-nums text-amber-900">
                        {enq.tentative_start ? formatTime(enq.tentative_start) : "—:—"}
                      </span>
                      <span className="mono text-[10px] uppercase tracking-widest font-bold text-amber-800 bg-amber-200/70 border border-amber-300 rounded-sm px-1.5 py-0.5">
                        Pending
                      </span>
                    </div>
                    <div className="text-[13px] font-semibold mt-0.5 text-amber-950">
                      {enq.customer_name || "—"}
                    </div>
                    {enq.notes && (
                      <div className="text-[11px] text-amber-900/80 line-clamp-2 mt-0.5">
                        {enq.notes}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sticky action bar */}
        <div className="border-t border-border bg-card px-4 sm:px-5 py-3 flex items-center gap-2"
             data-testid="day-drawer-actions">
          <Button
            className="flex-1 btn-cta gap-2"
            onClick={onAddBooking}
            data-testid="day-drawer-add-booking"
          >
            <Plus className="h-4 w-4" /> Add booking
          </Button>
          <Button
            variant="outline"
            className="flex-1 gap-2 border-amber-400 text-amber-900 hover:bg-amber-50"
            onClick={onAddEnquiry}
            data-testid="day-drawer-add-enquiry"
          >
            <Hourglass className="h-4 w-4" /> Add enquiry
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DrawerBookingCard({ b, onClick }) {
  const navigate = useNavigate();
  const stripe = staffStripe(b.staff_id);
  const loc = b.location || {};
  const addr = loc.address_line || loc.postcode || loc.eircode || "\u2014";
  const noteRaw = (b.notes || "").trim();
  const isOneOff = !!b.customer_is_one_off;
  const openProfile = (e) => {
    e.stopPropagation();
    if (b.customer_id) navigate(`/customers/${b.customer_id}`);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        // Trigger the card click on Enter / Space when focus is on the
        // card root (not a descendant interactive element like the
        // customer link). Lets keyboard users still expand/open.
        if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
          e.preventDefault();
          onClick?.(e);
        }
      }}
      className="w-full text-left bg-card border border-border hover:border-accent rounded-[8px] px-3 py-3 transition-colors group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      style={{ borderLeftWidth: 4, borderLeftColor: stripe }}
      data-testid={`day-drawer-booking-${b.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mono font-extrabold text-base tabular-nums tracking-tight">
            {formatTime(b.scheduled_start)} – {endTime(b)}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span
              role="link"
              tabIndex={0}
              onClick={openProfile}
              onKeyDown={(e) => { if (e.key === "Enter") openProfile(e); }}
              className="text-[15px] font-bold truncate hover:underline hover:text-accent transition-colors"
              title={`Open ${b.customer_name || "customer"} profile`}
              data-testid={`day-drawer-booking-${b.id}-customer-link`}
            >
              {b.customer_name}
            </span>
            {isOneOff && (
              <span className="mono text-[9px] uppercase tracking-widest font-bold bg-violet-100 text-violet-700 border border-violet-200 rounded-sm px-1 py-[1px]">
                1-off
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 truncate flex items-center gap-1">
            <MapPin className="h-3 w-3 shrink-0" />
            <span
              className="truncate select-text"
              onClick={(e) => e.stopPropagation()}
              data-testid={`day-drawer-booking-${b.id}-address`}
            >{linkifyText(addr)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span
            className="mono text-[10px] uppercase tracking-widest font-bold bg-secondary border border-border rounded-sm px-1.5 py-0.5"
            data-testid={`day-drawer-booking-${b.id}-staff`}
          >
            {(b.staff_name || "\u2014").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
          </span>
          <span
            className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest font-bold"
            data-testid={`day-drawer-booking-${b.id}-payment`}
          >
            <span className={`h-2 w-2 rounded-full ${paymentDotClass(b.payment_type_code)}`} />
            {paymentLabel(b.payment_type_code)}
          </span>
        </div>
      </div>
      {noteRaw && (
        <div
          className="mt-2 bg-yellow-100 border-l-4 border-yellow-400 rounded px-3 py-2"
          data-testid={`day-drawer-booking-${b.id}-notes`}
        >
          <div className="font-bold text-slate-900 text-sm">Notes</div>
          <div
            className="text-slate-800 text-sm whitespace-pre-line mt-1 select-text cursor-text"
            onClick={(e) => e.stopPropagation()}
          >
            {linkifyText(noteRaw)}
          </div>
        </div>
      )}
    </div>
  );
}

function DrawerTravelPill({ travel, prev, next }) {
  const minutes = Math.round(Number(travel.duration_min || 0));
  const km = Number(travel.distance_km || 0);
  const fromAddr = prev?.location?.address_line || "previous job";
  const toAddr = next?.location?.address_line || "this job";
  return (
    <div
      className="flex items-center gap-2 pl-3 -my-1 select-none"
      title={`${fromAddr} → ${toAddr}`}
      data-testid={`day-drawer-travel-${next.id}`}
    >
      <div className="h-4 w-px bg-sky-300" aria-hidden="true" />
      <span className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5">
        <Route className="h-3 w-3" />
        {minutes} min · {km} km
      </span>
    </div>
  );
}
